
import { SearchResult, InventoryItem } from "@/types/inventory";
import { apiConfig } from "@/config/api";
import { getAuthHeaders } from "@/utils/authUtils";
import { handleAPIError } from "@/utils/apiErrorHandler";

export const fetchInventoryData = async (searchTerm: string): Promise<SearchResult> => {
  const response = await fetch(apiConfig.endpoints.search, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ searchTerm })
  });

  const raw = await response.json();

  // Handle API errors with new backend structure
  if (!response.ok || raw.error) {
    const errorResult = handleAPIError(raw);
    if (errorResult.shouldRedirectToAuth) {
      // Let the calling component handle the redirect
      throw new Error('AUTH_REQUIRED');
    }
    throw new Error(raw.message || 'Search failed');
  }

  const rawItems = raw?.items ?? [];

  const items: InventoryItem[] = rawItems.map((item: any, i: number) => ({
    id: `${searchTerm.toLowerCase().replace(/\s+/g, "-")}-${i}`,
    name: item.name,
    category: "Whiskey",
    price: item.price,
    size: "750 ML",
    storeQty: parseInt(item.storeQty) || 0,
    warehouseQty: parseInt(item.warehouseQty) || 0,
    onOrderQty: parseInt(item.onOrderQty) || 0,
    location: "Various Utah Stores",
    lastChecked: new Date().toISOString(),
    stores: item.stores ?? []
  }));

  console.log("Fetched inventory data:", items);

  return {
    searchTerm,
    items,
    timestamp: new Date().toISOString(),
    searchCount: raw.searchCount,
    remainingSearches: raw.remainingSearches
  };
};

// Helper function to create standardized guest limit error with backend data
const createGuestLimitError = (backendResponse: any) => {
  console.log("🚫 [DEBUG] Creating guest limit error from backend response:", backendResponse);
  
  // Extract and normalize backend data with comprehensive logging
  const searchCount = backendResponse.searchCount;
  const maxSearches = backendResponse.maxSearches;
  const remainingSearches = backendResponse.remainingSearches;
  
  console.log("🚫 [DEBUG] Raw backend values:", {
    searchCount,
    maxSearches,
    remainingSearches,
    searchCountType: typeof searchCount,
    maxSearchesType: typeof maxSearches,
    remainingSearchesType: typeof remainingSearches
  });
  
  // Create normalized data with proper defaults
  const normalizedData = {
    searchCount: (typeof searchCount === 'number' && searchCount > 0) ? searchCount : 3,
    maxSearches: (typeof maxSearches === 'number' && maxSearches > 0) ? maxSearches : 3,
    remainingSearches: 0 // Always 0 when limit exceeded
  };
  
  console.log("🚫 [DEBUG] Normalized backend data:", normalizedData);
  
  const error = new Error(`Guest search limit exceeded. You've used all ${normalizedData.maxSearches} free searches. Please sign up for unlimited access.`);
  (error as any).backendData = normalizedData;
  (error as any).isGuestLimitError = true;
  
  console.log("🚫 [DEBUG] Final error object:", {
    message: error.message,
    backendData: (error as any).backendData,
    isGuestLimitError: (error as any).isGuestLimitError
  });
  
  return error;
};

export const performTraditionalSearch = async (searchTerm: string): Promise<SearchResult> => {
  console.log("🔄 [DEBUG] Falling back to traditional search method");
  
  try {
    const response = await fetch(apiConfig.endpoints.search, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ searchTerm })
    });

    let raw;
    try {
      raw = await response.json();
    } catch (parseError) {
      console.error("📦 [DEBUG] Failed to parse JSON response:", parseError);
      console.error("📦 [DEBUG] Response status:", response.status);
      console.error("📦 [DEBUG] Response text:", await response.text());
      throw new Error(`Server error (${response.status}): Unable to parse response`);
    }

    console.log("📦 [DEBUG] Traditional search response:", raw);
    console.log("📦 [DEBUG] Response status:", response.status);

    // Handle API errors with new backend structure
    if (!response.ok || raw.error) {
      console.error("📦 [DEBUG] API Error Response:", {
        status: response.status,
        error: raw.error,
        message: raw.message,
        requiresAuth: raw.requiresAuth,
        isGuestError: raw.isGuestError,
        searchCount: raw.searchCount,
        maxSearches: raw.maxSearches,
        remainingSearches: raw.remainingSearches
      });

      // Handle guest search limit exceeded with comprehensive logging
      if (raw.isGuestError && raw.error === 'GUEST_SEARCH_LIMIT_EXCEEDED') {
        console.log("🚫 [DEBUG] Guest search limit exceeded, creating standardized error");
        throw createGuestLimitError(raw);
      }

      const errorResult = handleAPIError(raw);
      if (errorResult.shouldRedirectToAuth) {
        // Let the calling component handle the redirect
        throw new Error('AUTH_REQUIRED');
      }
      throw new Error(raw.message || `Search failed with status ${response.status}`);
    }

    const rawItems = raw?.items ?? [];

    // Check for too_many_results in traditional search response
    if (raw.too_many_results) {
      console.log("🚫 [DEBUG] Traditional search detected too many results");
      return {
        searchTerm,
        items: [],
        timestamp: new Date().toISOString(),
        too_many_results: true,
        totalItemsFromAPI: raw.totalItemsFromAPI || raw.count || 0,
        maxAllowedItems: raw.maxAllowedItems || raw.threshold || 10,
        searchCount: raw.searchCount,
        remainingSearches: raw.remainingSearches
      };
    }

    const items: InventoryItem[] = rawItems.map((item: any, i: number) => ({
      id: `${searchTerm.toLowerCase().replace(/\s+/g, "-")}-${i}`,
      name: item.name,
      category: "Whiskey",
      price: item.price,
      size: "750 ML",
      storeQty: parseInt(item.storeQty) || 0,
      warehouseQty: parseInt(item.warehouseQty) || 0,
      onOrderQty: parseInt(item.onOrderQty) || 0,
      location: "Various Utah Stores",
      lastChecked: new Date().toISOString(),
      stores: item.stores ?? []
    }));

    const result = {
      searchTerm,
      items,
      timestamp: new Date().toISOString(),
      searchCount: raw.searchCount,
      remainingSearches: raw.remainingSearches
    };
    
    console.log(`📦 [DEBUG] Traditional search created ${items.length} items`);
    return result;
  } catch (error) {
    console.error("❌ [DEBUG] Traditional search failed:", error);
    console.error("❌ [DEBUG] Error details:", {
      name: error.name,
      message: error.message,
      stack: error.stack,
      isGuestLimitError: (error as any).isGuestLimitError,
      backendData: (error as any).backendData
    });
    throw error;
  }
};
